import React from 'react'
import { ComponentReader } from '../reader'

/**
 * AldooSlotRenderer
 * Renders a layout slot with its assigned component and data
 *
 * @param {Object} slot - The slot data from CMS
 * @param {string} slot.id - Slot identifier
 * @param {Array} slot.component - Reference to AldooComponent [{_id, label}]
 * @param {Object} slot.data - Data to pass to the component
 * @param {Object} slot.position - Position configuration {x, y}
 * @param {Object} slot.size - Size configuration {width, height}
 */
const AldooSlotRenderer = ({ slot, pageData, layoutData }) => {
  if (!slot) {
    console.warn('AldooSlotRenderer: No slot data provided')
    return null
  }

  // Extract component reference
  const componentRef = slot.component?.[0]


  if (!componentRef || !componentRef._id) {
    console.warn(`AldooSlotRenderer: Slot "${slot.id}" has no component assigned`)
    return null
  }

  // Get the component ID from CMS data
  // The component reference may have data directly or nested in 'content'
  const componentId = componentRef.content?.id || componentRef.id || componentRef.label


  if (!componentId) {
    console.warn(`AldooSlotRenderer: Could not resolve component ID for slot "${slot.id}"`, componentRef)
    return null
  }

  // Look up the component in the registry
  const aldooComponent = ComponentReader.getComponent(componentId)


  if (!aldooComponent) {
    console.warn(`AldooSlotRenderer: Component "${componentId}" not found in registry for slot "${slot.id}"`)
    return (
      <div className="p-4 border-2 border-dashed border-admin_error bg-admin_error/10 rounded">
        <p className="text-admin_error text-sm">
          Component "{componentId}" not registered
        </p>
        <p className="text-admin_text_secondary text-xs mt-2">
          Slot ID: {slot.id}
        </p>
      </div>
    )
  }

  // Get the React component
  const Component = aldooComponent.component

  if (!Component) {
    console.warn(`AldooSlotRenderer: Component "${componentId}" has no component property`)
    return null
  }

  // Prepare props from slot data
  // Handle both object and array format for data
  let slotData = {}
  if (slot.data) {
    if (Array.isArray(slot.data)) {
      // Convert array format [{ key: 'foo', value: 'bar' }] to object { foo: 'bar' }
      slotData = slot.data.reduce((acc, item) => {
        if (item.key) {
          acc[item.key] = item.value
        }
        return acc
      }, {})
    } else {
      slotData = slot.data
    }
  }

  // Filter out __empty__ placeholder values and convert to undefined
  const cleanedSlotData = {}
  Object.keys(slotData).forEach(key => {
    const value = slotData[key]
    if (value !== '__empty__') {
      cleanedSlotData[key] = value
    }
  })

  const componentProps = {
    ...cleanedSlotData,
    // Pass context data
    _slot: {
      id: slot.id,
      position: slot.position,
      size: slot.size,
    },
    _page: pageData,
    _layout: layoutData,
  }


  // Apply CSS Grid positioning based on slot position and size
  // Handle position being an array (corrupted data)
  let position = slot.position || { row: 0, col: 0 }
  if (Array.isArray(position)) {
    position = position.reduce((acc, item) => {
      if (item.key) acc[item.key] = parseInt(item.value) || 0
      return acc
    }, { row: 0, col: 0 })
  }

  // Handle size being an array (corrupted data)
  let size = slot.size || { rows: 2, cols: 3 }
  if (Array.isArray(size)) {
    size = size.reduce((acc, item) => {
      if (item.key) acc[item.key] = parseInt(item.value) || 0
      return acc
    }, { rows: 2, cols: 3 })
  }

  const containerStyle = {
    // Grid column: start at col+1 (CSS Grid is 1-indexed), span cols
    gridColumn: `${position.col + 1} / span ${size.cols}`,
    // Grid row: use row position but let height be natural (auto span)
    gridRow: `${position.row + 1}`,
  }

  return (
    <div
      className="aldoo-slot"
      data-slot-id={slot.id}
      data-component-id={componentId}
      style={{
        ...containerStyle,
        width: '100%',
      }}
    >
      <Component {...componentProps} />
    </div>
  )
}

export default AldooSlotRenderer